import React, {FC, useEffect} from 'react';

import DateList from './DateList';
import {Callbacks, useState} from './reducer';
import TimeList from './TimeList';
import {DateSlot, TimeSlot} from './types';

import * as styles from './styles.module.scss';

export enum TPStyle {
	condensed = 'condensed',
	sectioned = 'sectioned',
}

interface TimePickerProps {
	dateSlots: DateSlot[]
	style: TPStyle
	initial?: string
	loading?: boolean
	callbacks: Callbacks
}

const TimeSlotPicker: FC<TimePickerProps> = ({
	dateSlots,
	style,
	initial,
	loading,
	callbacks,
}) => {
	const [state, dispatch] = useState(callbacks);

	// On every change to date slots, forward through to state
	// and initialize selection if not currently present
	useEffect(() => {
		dispatch({type: 'SET-SLOTS', slots: dateSlots});

		// If no dates available, exit (nothing to do)
		if (dateSlots.length === 0) {
			return;
		}

		// If date was auto-set, see if it could use an update
		if (state.date) {
			if (state.automatic) {
				// If there's a date before our current date
				// issue another automatic set
				//
				// This happens because we issue multiple API
				// queries in parallel & get out-of-order replies
				if (dateSlots[0].date < state.date.date) {
					dispatch({
						type: 'SET-DATE',
						date: dateSlots[0],
						automatic: true, // automatic => skip "scroll into view"
					});
				}
			}
			return;
		}

		// If we have a desired initial, try to find it
		if (initial) {
			const desired = dateSlots.find((d) => d.date === initial);
			if (desired) {
				dispatch({
					type: 'SET-DATE',
					date: desired,
				});
				return;
			}
		}

		// If no desired initial OR done loading, set to first
		if (!initial || !loading) {
			dispatch({
				type: 'SET-DATE',
				date: dateSlots[0],
				automatic: true, // automatic => skip "scroll into view"
			});
		}
	}, [dateSlots, loading]);

	// Pass custom props to time picker depending on style
	const timeProps = style === TPStyle.sectioned ? {
		timeSlots: state.date?.timesBase || [],
		sectioned: true,
	} : {
		timeSlots: state.date?.timesLeft || [],
		sectioned: false,
	};

	return (
		<div className={styles.picker}>
			<DateList
				dateSlots={state.dateSlots}
				selected={state.date}
				scroll={!state.automatic}
				loading={Boolean(loading)}
				onSelect={(index: number) => dispatch({
					type: 'SET-DATE',
					date: state.dateSlots[index],
				})}
			/>
			<TimeList
				{...timeProps}
				selected={state.time}
				onSelect={(time?: TimeSlot) => dispatch({
					type: 'SET-TIME',
					time,
				})}
			/>
		</div>
	);
};

export default TimeSlotPicker;
